import React, { useEffect, useState } from "react";
import { Table, Input, Space, Popconfirm, Modal, Radio, Select, DatePicker, Checkbox, Button, ConfigProvider, message } from "antd";
import { SearchOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import viVN from "antd/es/locale/vi_VN";
import dayjs from "dayjs";
import { $api } from "../../services/service";
import fileDownload from 'js-file-download';
import moment from 'moment-timezone';
import DefaultLayout from '../../layouts/DefaultLayout/DefaultLayout';
import { _AUTH } from '../../constants/_auth';

const { Option } = Select;

const Retake: React.FC = () => {
    let role = localStorage.getItem(_AUTH.ROLE) !== null && localStorage.getItem(_AUTH.ROLE) != 'Teacher' && localStorage.getItem(_AUTH.ROLE) != 'TeacherStaff';
    const [faculties, setFaculties] = useState<any[]>([]);
    const [facultyId, setFacultyId] = useState<string | null>(null);
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const [selectedSemester, setSelectedSemester] = useState<number | null>(null);
    const [options, setOptions] = useState<string[]>([
        "IncludeMidAvg",
        "IncludeAvg",
        "IncludeAttendance",
    ]);
    const [loading, setLoading] = useState(false);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [loadingSubjects, setLoadingSubjects] = useState(false);

    const [list, setList] = useState<any[]>([]);
    const [loadingList, setLoadingList] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filterType, setFilterType] = useState<number | null>(null);

    // Modal thêm đăng ký
    const [studentModalOpen, setStudentModalOpen] = useState(false);
    const [subjectModalOpen, setSubjectModalOpen] = useState(false);
    const [studentSearch, setStudentSearch] = useState("");
    const [students, setStudents] = useState<any[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<any | null>(null);

    const [subjectSearch, setSubjectSearch] = useState("");
    const [subjects, setSubjects] = useState<any[]>([]);
    const [selectedSubject, setSelectedSubject] = useState<any | null>(null);

    const [newType, setNewType] = useState<number>(0);
    const [newNotes, setNewNotes] = useState<string | null>(null);

    // State for editing notes on an existing record
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editNotes, setEditNotes] = useState<string>("");
    const [editItem, setEditItem] = useState<any | null>(null);

    const [pagination, setPagination] = useState({
        pageIndex: 1,
        pageSize: 20,
        total: 0,
    });


    const fetchList = async () => {
        if (!role) return;
        setLoadingList(true);
        try {
            const res: any = await $api.subjectSpecial.getPaging({
                freeTextSearch: searchText,
                type: filterType,
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
            });
            setList(res?.result?.items ?? []);
            setPagination((prev) => ({
                ...prev,
                total: res?.result?.totalRecord ?? 0,
            }));
        } catch (err) {
            message.error("Không tải được danh sách");
        } finally {
            setLoadingList(false);
        }
    };

    const handleSearch = async () =>{
        setPagination((prev) => ({
            ...prev,
            pageIndex: 1,
        }));
        fetchList();
    }

    const handleDelete = async (id: string) => {
        try {
            await $api.subjectSpecial.delete({ id });
            message.success("Xóa thành công");
            fetchList();
        } catch (err) {
            message.error("Xóa thất bại");
        }
    };

    // Save updated notes for an existing record
    const handleUpdateNotes = async () => {
        if (!editItem) return;
        try {
            const payload: any = { id: editItem.id, notes: editNotes ?? "" };
            // Include these if backend requires same payload shape as create
            if (editItem.subjectId) payload.subjectId = editItem.subjectId;
            if (editItem.studentId) payload.studentId = editItem.studentId;
            if (typeof editItem.type !== 'undefined') payload.type = editItem.type;

            await $api.subjectSpecial.save(payload);
            message.success("Cập nhật ghi chú thành công");
            setEditModalOpen(false);
            setEditItem(null);
            setEditNotes("");
            fetchList();
        } catch (e: any) {
            let errMsg;
            let type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Đã có lỗi xảy ra, xin vui lòng thử lại';
            }
            message.error(errMsg);
        }
    };

    const handleSearchStudent = async () => {
        try {
            setLoadingStudents(true);
            const res: any = await $api.student.getPaging({
                freeTextSearch: studentSearch
            });
            setStudents(res?.result?.items ?? []);
        } catch {
            message.error("Không tìm thấy SV");
        } finally {
            setLoadingStudents(false);
        }
    };

    const handleSearchSubject = async () => {
        if (!selectedStudent) {
            message.error("Chọn sinh viên trước");
            return;
        }
        try {
            setLoadingSubjects(true);
            const res: any = await $api.subjectSpecial.getPagingSubject({
                freeTextSearch: subjectSearch,
                classId: selectedStudent.classId,
            });
            setSubjects(res?.result?.items ?? []);
        } catch {
            message.error("Không tìm thấy môn học");
        } finally {
            setLoadingSubjects(false);
        }
    };

    const handleSave = async () => {
        if (!selectedStudent || !selectedSubject) {
            message.error("Chọn SV và môn học");
            return;
        }
        try {
            await $api.subjectSpecial.save({
                subjectId: selectedSubject.id,
                studentId: selectedStudent.id,
                type: newType,
                notes: newNotes ?? ""
            });
            message.success("Lưu thành công");
            setStudentModalOpen(false);
            setSubjectModalOpen(false);
            setSelectedStudent(null);
            setSelectedSubject(null);
            setNewNotes(null);
            setSubjects([]);
            fetchList();
        } catch (e: any) {
            let errMsg;
            let type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Đã có lỗi xảy ra, xin vui lòng thử lại';
            }
            message.error(errMsg);
        }
    };
    useEffect(() => {
        fetchList();
    }, [filterType, pagination.pageIndex, pagination.pageSize]);


    useEffect(() => {
        const fetchFaculties = async () => {
            try {
                const res: any = await $api.faculties.getPaging({});
                if (res && res.result?.items?.length > 0) {
                    const sorted = [...res.result.items].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    );
                    setFaculties(sorted);
                    // setFacultyId(sorted[0].id);
                }
            } catch (err) {
                message.error("Failed to load faculties");
            }
        };
        fetchFaculties();
    }, []);

    const handleExport = async () => {

        if (!selectedYear){
            message.error("Vui lòng chọn năm học");
            return;
        }
        const req = {
            facultyId: facultyId,
            year: selectedYear,
            semester: selectedSemester,
            includeMidAvg: options.includes("IncludeMidAvg"),
            includeAvg: options.includes("IncludeAvg"),
            includeAttendance: options.includes("IncludeAttendance"),
        };

        try {
            setLoading(true);
            const res: any = await $api.schedule.exportStudentFail(req);
            fileDownload(res, `Danh-sach-SV-hoc-lai-${moment((new Date())).format('DD/MM/YYYY')}.xlsx`);
            message.success("Export request sent successfully");
        } catch (err) {
            message.error("Export failed");
        } finally {
            setLoading(false);
        }
    };

    // Define columns with a relaxed type to avoid TS align typing incompatibilities
    const columns: any[] = [
        {
            title: "STT",
            render: (_: any, __: any, idx: number) => idx + 1,
            align: "center",
            width: 70,
        },
        {
            title: "MSSV",
            dataIndex: "userInternalId",
            align: "left",
        },
        {
            title: "Tên SV",
            dataIndex: "studentName",
            align: "left",
        },
        {
            title: "Tên môn",
            dataIndex: "subjectName",
            align: "left",
            render: (_: any, record: any) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    <i className="text-primary">({record.creditPoint}TC)</i> {record.subjectName}
                </div>
            ),
        },
        {
            title: "Ghi chú",
            dataIndex: "notes",
            align: "left",
            render: (text: string) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word", textAlign: "justify" }}>
                    {text}
                </div>
            ),
        },
        {
            title: "Điểm",
            dataIndex: "type",
            align: "center",
            width: 120,
            render: (t: number) => (t === 0 ? "Miễn môn" : "Học lại"),
        },
        {
            title: "Thao tác",
            align: "center",
            width: 150,
            render: (record: any) => (
                <Space>
                    <Button
                        className="focus:outline-none focus:ring-0 p-0"
                        type="link"
                        onClick={() => {
                            setEditItem(record);
                            setEditNotes(record?.notes ?? "");
                            setEditModalOpen(true);
                        }}
                    >
                        <EditOutlined /> Sửa
                    </Button>
                    <Popconfirm
                        title="Xác nhận xóa đăng ký?"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button className="focus:outline-none focus:ring-0 p-0" type="link" danger>
                            <DeleteOutlined />
                            Xóa
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];


    return (
        <DefaultLayout>
            <div className='page mark-board'>
                <div className='page__header'>
                    <div className='___title flex items-center'>
                        <div className='___title__decorate' />
                        <div className='___title__box'>
                            <div>Quản lý học lại</div>
                        </div>
                    </div>
                </div>
                <div className='page__content bg-white mt-3 p-4 space-y-4'>
                    <Select
                        className='w-64'
                        placeholder='- Chọn khoa -'
                        value={facultyId ?? undefined}
                        onChange={(val) => setFacultyId(val)}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.children as unknown as string)
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    >
                        {faculties.map((f) => (
                            <Option key={f.id} value={f.id}>
                                {f.name}
                            </Option>
                        ))}
                    </Select>

                    <div className='flex space-x-4'>
                        <ConfigProvider locale={viVN}>
                            <DatePicker
                                picker='year'
                                className='w-40'
                                placeholder='Chọn năm học'
                                value={selectedYear ? dayjs(selectedYear, 'YYYY') : null}
                                onChange={(date) => {
                                    setSelectedYear(date ? date.format('YYYY') : null);
                                    setSelectedSemester(null);
                                }}
                                format={(value) => {
                                    if (!value) return '';
                                    const year = value.year();
                                    return `${year} - ${year + 1}`;
                                }}
                            />
                        </ConfigProvider>

                        <Select
                            placeholder='-- Chọn học kỳ --'
                            className='w-40'
                            allowClear
                            value={selectedSemester ?? undefined}
                            onChange={(val) => setSelectedSemester(val ?? null)}
                            disabled={!selectedYear}
                        >
                            <Option value={1}>Học kỳ 1</Option>
                            <Option value={2}>Học kỳ 2</Option>
                        </Select>
                    </div>

                    <Checkbox.Group
                        options={[
                            { label: 'Điểm danh', value: 'IncludeAttendance' },
                            { label: 'TBGK', value: 'IncludeMidAvg' },
                            { label: 'Điểm số', value: 'IncludeAvg' }
                        ]}
                        value={options}
                        onChange={(vals) => setOptions(vals as string[])}
                    />

                    <div>
                        <Button
                            type='primary'
                            className="focus:outline-none focus:ring-0"
                            onClick={handleExport}
                            loading={loading}
                        >
                            Xuất DSSV học lại
                        </Button>
                    </div>
                </div>
                { role  ?
                    <div className="page__content bg-white mt-3 p-4 space-y-4">

                        <h5 className="font-bold">Danh sách đăng ký học lại, miễn môn</h5>
                        <div className="flex items-center space-x-4">
                            <Input
                                placeholder="Tìm kiếm..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onPressEnter={handleSearch}
                                suffix={<SearchOutlined onClick={handleSearch} />}
                                className="w-64"
                            />

                            <Select
                                placeholder="-- Loại --"
                                className="w-40"
                                allowClear
                                value={filterType ?? undefined}
                                onChange={(val) => setFilterType(val ?? null)}
                            >
                                <Option value={0}>Miễn môn</Option>
                                <Option value={1}>Học lại</Option>
                            </Select>

                            <Button
                                type="primary"
                                className="focus:outline-none focus:ring-0"
                                icon={<PlusOutlined />}
                                onClick={() => setStudentModalOpen(true)}
                            >
                                Thêm đăng ký
                            </Button>
                        </div>

                        <Table
                            rowKey="id"
                            columns={columns}
                            dataSource={list}
                            loading={loadingList}
                            pagination={{
                                ...pagination,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50", "100"],
                                onChange: (page, size) => {
                                    setPagination((prev) => ({
                                        ...prev,
                                        pageIndex: page,
                                        pageSize: size || prev.pageSize,
                                    }));
                                },
                            }}
                        />
                    </div>
                    : ''
                }

            </div>
            <Modal
                title="Chọn sinh viên"
                open={studentModalOpen}
                width={600}
                onCancel={() => setStudentModalOpen(false)}
                onOk={() => {
                    if (selectedStudent) {
                        setStudentModalOpen(false);
                        setSubjectModalOpen(true);
                    } else {
                        message.error("Chọn SV trước");
                    }
                }}
            >
                <Input.Search
                    className="mb-2"
                    placeholder="Nhập tên/MSSV"
                    enterButton="Tìm"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    onSearch={handleSearchStudent}
                />
                <Table
                    rowKey="id"
                    dataSource={students}
                    loading={loadingStudents}
                    columns={[
                        { title: "MSSV", dataIndex: "userInternalId", align: "left" },
                        { title: "Tên SV", dataIndex: "fullName", align: "left" },
                        { title: "Lớp", dataIndex: "className", align: "left" }
                    ]}
                    rowSelection={{
                        type: "radio",
                        onChange: (_, rows) => {
                            setSelectedStudent(rows[0]);
                            setSubjects([]);
                            setSelectedSubject(null);
                        }
                    }}
                    pagination={false}
                    size="small"
                />
            </Modal>

            <Modal
                title="Chọn môn học"
                open={subjectModalOpen}
                width={600}
                loading={loadingSubjects}
                onCancel={() => {
                    setSubjectModalOpen(false);
                    setStudentModalOpen(true);
                }}
                onOk={handleSave}
            >
                <Input.Search
                    placeholder="Nhập tên/mã môn"
                    enterButton="Tìm"
                    className="mb-2"
                    value={subjectSearch}
                    onChange={(e) => setSubjectSearch(e.target.value)}
                    onSearch={handleSearchSubject}
                />
                <Table
                    rowKey="id"
                    dataSource={subjects}
                    columns={[
                        {
                            title: "Mã môn / TC",
                            dataIndex: "subjectCode",
                            align: "left",
                            render: (_: any, record: any) => (
                                <div>
                                    <div>{record.subjectCode}</div>
                                    <div className="text-muted italic">Tín chỉ: {record.creditPoint}</div>
                                </div>
                            ),
                        },
                        { title: "Tên môn", dataIndex: "name", align: "left" },
                        { title: "Khoa", dataIndex: "facultyName", align: "left" }
                    ]}
                    rowSelection={{
                        type: "radio",
                        onChange: (_, rows) => setSelectedSubject(rows[0])
                    }}
                    pagination={false}
                    size="small"
                />

                <div className="mt-4">
                    <Input.TextArea
                        value={newNotes ?? ""}
                        onChange={(e) => setNewNotes(e.target.value)}
                        placeholder="Nhập ghi chú (nếu có)"
                        rows={3}
                    />
                </div>
                <div className="flex justify-content-end mt-3">
                    <span className="mr-3 text-bold">Loại đăng ký:</span>
                    <Radio.Group
                        value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                    >
                        <Radio value={0}>Miễn môn</Radio>
                        <Radio value={1}>Học lại</Radio>
                    </Radio.Group>
                </div>
            </Modal>

            {/* Modal edit notes */}
            <Modal
                title="Cập nhật ghi chú"
                open={editModalOpen}
                width={600}
                onCancel={() => {
                    setEditModalOpen(false);
                    setEditItem(null);
                    setEditNotes("");
                }}
                onOk={handleUpdateNotes}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Input.TextArea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Nhập ghi chú"
                    rows={4}
                />
            </Modal>
        </DefaultLayout>

    );
};

export default Retake;
